/**
 * Starts the data flow into the store: the live hub (GET /api/state + SSE) or,
 * with ?mock=1, the demo fixture and simulator. Called once by main.tsx.
 */
import type { AggregateState } from '@orgus/protocol';
import { CLIENT_ID, apiGet, bootstrapAuth, setActionHandler, toUiError } from './api.ts';
import { createLiveBatcher } from './batch.ts';
import { createMockActionHandler, createMockHistory, createMockState, isMockRequested, isMockStill, startMockSimulator } from './mock.ts';
import type { MockSink } from './mock.ts';
import { mockScaleRequested, scaleMockState, startScaleSimulator } from './mockScale.ts';
import { routeSwitch } from './routeSwitch.ts';
import { useOrgus } from './store.ts';
import { connectStream } from './stream.ts';
import type { ProbeResult } from './stream.ts';
import { useMe } from '../features/auth/useMe.ts';

/** After the stream drops: is the hub unreachable, failing, or refusing this browser? */
async function probeHub(): Promise<ProbeResult> {
  try {
    const r = await fetch('/api/me', { credentials: 'same-origin', cache: 'no-store' });
    if (r.status === 401) return 'signed-out';
    // A proxy in front of the hub (the dev server) answers 502 to 504 when it cannot reach it.
    if (r.status === 502 || r.status === 503 || r.status === 504) return 'offline';
    if (r.status >= 500) return 'hub-error';
    return null;
  } catch {
    return 'offline';
  }
}

let stopCurrent: (() => void) | null = null;
/** Full states the stream has applied since the live data flow started. */
let streamStates = 0;

function mockSink(): MockSink {
  const st = useOrgus.getState;
  return {
    patch: (p) => st().applyPatch(p),
    event: (e) => st().applyEvent(e),
    audit: (a) => st().applyAudit(a),
    getState: () => st(),
  };
}

/**
 * The simulators' sink: through the same batcher as the live stream, so demo data behaves like a
 * hub's (one update per batch, held while a page opens). Actions keep the direct sink: they read
 * their own change back at once.
 */
function batchedMockSink(): { sink: MockSink; stop: () => void } {
  const batcher = createLiveBatcher((b) => useOrgus.getState().applyLive(b), { gate: routeSwitch });
  return {
    sink: { patch: batcher.patch, event: batcher.event, audit: batcher.audit, getState: () => useOrgus.getState() },
    stop: () => batcher.stop(),
  };
}

function startMock(): () => void {
  const store = useOrgus.getState();
  store.setMode('mock');
  store.setConnection('mock');
  const now = Date.now();
  const scale = mockScaleRequested();
  const big = scale !== 'small';
  const state = scale !== 'small' ? scaleMockState(createMockState(now), now, scale) : createMockState(now);
  store.setAggregate(state);
  const machineHistory = createMockHistory(state, now);
  useOrgus.setState((st) => ({ machineHistory, full: { ...st.full, machineHistory } }));
  const sink = mockSink();
  setActionHandler(createMockActionHandler(sink));
  // ?still=1 holds the demo data still (visual tests); otherwise it changes every 1 to 2 s.
  const live = batchedMockSink();
  const stop = isMockStill() ? () => {} : startMockSimulator(live.sink);
  const stopScale = big && !isMockStill() ? startScaleSimulator(live.sink) : () => undefined;
  return () => {
    stop();
    stopScale();
    live.stop();
    setActionHandler(null);
  };
}

/**
 * Fetch the full state once (used on boot and by "Try again" on the load error). Applied only
 * while the stream has not delivered a state of its own: once it has, the stream's state plus
 * the patches after it are newer than any GET, and a GET answering late (the state is
 * megabytes at scale) would roll them back and bring removed sessions back as ghosts.
 */
export async function loadState(): Promise<void> {
  const store = useOrgus.getState();
  try {
    const s = await apiGet<AggregateState>('/api/state');
    if (streamStates > 0) return;
    store.setAggregate(s);
  } catch (e) {
    if (useOrgus.getState().phase !== 'ready') store.setPhase('error', toUiError(e));
    throw e;
  }
}

function startLive(): () => void {
  const store = useOrgus.getState();
  store.setMode('live');
  streamStates = 0;
  let stopStream: (() => void) | null = null;
  let cancelled = false;
  /** Set once the hub ended the stream for good: restarts the data flow when this browser is signed in again. */
  let unwatchSignIn: (() => void) | null = null;
  // Patches, events, audit entries and module events reach the store at most every LIVE_BATCH_MS, in one update.
  const batcher = createLiveBatcher((b) => useOrgus.getState().applyLive(b), { gate: routeSwitch });
  void (async () => {
    // The link as it was in the address bar, before bootstrapAuth strips it: shown again if it fails.
    const link = typeof window !== 'undefined' ? window.location.href : undefined;
    try {
      await bootstrapAuth();
    } catch (e) {
      store.setPhase('error', toUiError(e, { what: 'Could not sign in with that link.', next: 'Copy the sign-in link from the hub terminal again.' }));
      // The sign-in screen shows why (UX-10): a link that is wrong or from an earlier start.
      const status = (e as { status?: number }).status;
      useMe.getState().setSigninError({ code: status === 429 ? 'rate-limited' : 'bad-link', login: null, ...(link ? { link } : {}) });
      void useMe.getState().load();
      return;
    }
    if (cancelled) return;
    // The stream sends the full state first; the GET makes the first paint independent of SSE.
    loadState().catch(() => undefined);
    stopStream = connectStream(
      {
        onState: (s) => {
          // Anything queued is older than this state: apply it first, then replace.
          batcher.flush();
          streamStates++;
          useOrgus.getState().setAggregate(s);
        },
        onPatch: (p) => batcher.patch(p),
        onEvent: (e) => batcher.event(e),
        onAudit: (a) => batcher.audit(a),
        onLog: (c) => useOrgus.getState().appendLog(c),
        // Through the batcher, so a module update is never applied ahead of the patches before it.
        onExt: (e) => batcher.ext(e),
        onStatus: (c) => {
          useOrgus.getState().setConnection(c);
          // The hub stopped accepting this browser: the sign-in screen takes over.
          if (c === 'signed-out' && useMe.getState().status === 'signed-in') void useMe.getState().load();
        },
        // SD-2: the hub ended the stream. A new role: ask who we are again (the stream has
        // reconnected once). Anything else: signed out, with the reason the sign-in screen shows.
        onAuthEnded: (reason) => {
          if (reason === 'role-changed') {
            void useMe.getState().load();
            return;
          }
          useMe.getState().setStreamEnded(reason);
          // Signed in again without a page load (a sign-in that does not restart the data flow itself): start it.
          unwatchSignIn ??= useMe.subscribe((s) => {
            if (s.status === 'signed-in' && !cancelled) startData();
          });
        },
      },
      // The client id lets the hub send this tab only the log streams it opened.
      { url: `/api/stream?client=${CLIENT_ID}`, probe: probeHub },
    );
  })();
  return () => {
    cancelled = true;
    unwatchSignIn?.();
    stopStream?.();
    batcher.stop();
  };
}

/** Start (or restart) the data flow. Returns a stop function. */
export function startData(opts: { mock?: boolean } = {}): () => void {
  stopCurrent?.();
  useOrgus.getState().reset();
  const mock = opts.mock ?? isMockRequested();
  stopCurrent = mock ? startMock() : startLive();
  return () => {
    stopCurrent?.();
    stopCurrent = null;
  };
}
